import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  IndianRupee,
  FileText,
  TrendingUp,
  TrendingDown,
  CreditCard,
  AlertCircle,
  Users,
  UserPlus,
  Receipt,
  RefreshCw
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import financialAnalyticsService from '../../../api/crm/analytics/financialAnalyticsService';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import { formatDate } from '../../../utils/stringUtils';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import fieldSalesService from '../../../api/crm/field-sales/fieldSalesService';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';

const DATE_RANGE_OPTIONS = [
  { value: 'all_time', label: 'All Data' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_15_days', label: 'Last 15 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' }
];

const CHART_COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16', '#6366f1', '#f97316'];

const formatSalesAxis = (value) => {
  const num = Number(value) || 0;
  if (num >= 10000000) return `${(num / 10000000).toFixed(1)}Cr`;
  if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return String(num);
};

const ChartCard = ({ title, subtitle, children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm p-4 md:p-6 ${className}`}>
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    {subtitle && <p className="text-xs text-gray-500 mt-0.5 mb-4">{subtitle}</p>}
    {!subtitle && <div className="mb-4" />}
    {children}
  </div>
);

const SectionLoader = ({ height = 'h-[280px]' }) => (
  <div className={`${height} w-full flex items-center justify-center text-sm text-gray-400`}>
    Loading...
  </div>
);

const EmptyChart = ({ message = 'No data available' }) => (
  <div className="h-[280px] w-full flex items-center justify-center text-sm text-gray-400">
    {message}
  </div>
);

const KpiSkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
    <div className="h-8 bg-gray-200 rounded w-20" />
  </div>
);

const TableSkeleton = ({ rows = 5, cols = 4 }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} className="animate-pulse">
        {Array.from({ length: cols }).map((__, j) => (
          <td key={j} className="px-4 py-3">
            <div className="h-4 bg-gray-200 rounded w-full max-w-[120px]" />
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

const FinancialAnalytics = () => {
  const { user } = useAuth();
  const isManager = user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER;
  const isExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE || user?.role === USER_ROLES.EXPORT_EXECUTIVE || user?.role === USER_ROLES.BUSINESS_RELATIONSHIP_MANAGER || user?.role === USER_ROLES.BUSINESS_DEVELOPMENT_EXECUTIVE;
  const currentUserId = user?._id || user?.id;
  const currentUserName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me';
  const isManagerScope = isManager && currentUserId;

  const [dateRange, setDateRange] = useState('last_30_days');
  const [customRange, setCustomRange] = useState({ startDate: '', endDate: '' });
  const [managers, setManagers] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [selectedManager, setSelectedManager] = useState(() => (isManagerScope ? currentUserId : 'all'));
  const [selectedExecutive, setSelectedExecutive] = useState('all');
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [revenueTrendLoading, setRevenueTrendLoading] = useState(true);
  const [revenueVsCollections, setRevenueVsCollections] = useState([]);
  const [revenueVsLoading, setRevenueVsLoading] = useState(true);
  const [invoiceTrend, setInvoiceTrend] = useState([]);
  const [invoiceTrendLoading, setInvoiceTrendLoading] = useState(true);
  const [invoiceDistribution, setInvoiceDistribution] = useState([]);
  const [invoiceDistLoading, setInvoiceDistLoading] = useState(true);
  const [collectionTrend, setCollectionTrend] = useState([]);
  const [collectionTrendLoading, setCollectionTrendLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);
  const [topPaymentCustomers, setTopPaymentCustomers] = useState([]);
  const [topPaymentLoading, setTopPaymentLoading] = useState(true);
  const [outstandingAging, setOutstandingAging] = useState([]);
  const [agingLoading, setAgingLoading] = useState(true);
  const [topOutstanding, setTopOutstanding] = useState([]);
  const [topOutstandingLoading, setTopOutstandingLoading] = useState(true);
  const [topRevenueCustomers, setTopRevenueCustomers] = useState([]);
  const [topRevenueLoading, setTopRevenueLoading] = useState(true);
  const [customerDistribution, setCustomerDistribution] = useState([]);
  const [customerDistLoading, setCustomerDistLoading] = useState(true);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [monthlySummaryLoading, setMonthlySummaryLoading] = useState(true);
  const [latestInvoices, setLatestInvoices] = useState([]);
  const [latestInvoicesLoading, setLatestInvoicesLoading] = useState(true);
  const [latestPayments, setLatestPayments] = useState([]);
  const [latestPaymentsLoading, setLatestPaymentsLoading] = useState(true);
  const [largestPayments, setLargestPayments] = useState([]);
  const [largestPaymentsLoading, setLargestPaymentsLoading] = useState(true);
  const [executiveSummary, setExecutiveSummary] = useState([]);
  const [executiveSummaryLoading, setExecutiveSummaryLoading] = useState(false);

  const abortRef = useRef(null);
  const showExecutiveSummary = !isExecutive && selectedManager !== 'all';

  const filterParams = useMemo(() => {
    const isCustom = dateRange === 'custom' && customRange.startDate && customRange.endDate;
    return {
      range: isCustom ? 'custom' : dateRange,
      startDate: isCustom ? customRange.startDate : '',
      endDate: isCustom ? customRange.endDate : '',
      managerId: selectedManager,
      executiveId: selectedExecutive,
      topN: 10,
      limit: 10
    };
  }, [dateRange, customRange.startDate, customRange.endDate, selectedManager, selectedExecutive]);

  useEffect(() => {
    if (isExecutive || isManager) return;
    const fetchManagers = async () => {
      try {
        const res = await fieldSalesService.getSalesReps({ role: 'sales_manager,export_manager' });
        if (res.success) setManagers(res.data);
      } catch (err) {
      }
    };
    fetchManagers();
  }, [isExecutive, isManager]);

  useEffect(() => {
    if (isExecutive) return;
    const fetchExecutives = async () => {
      try {
        const params = { role: 'sales_executive,export_executive,business_relationship_manager,business_development_executive' };
        if (isManagerScope) {
          params.managerId = currentUserId;
        } else if (selectedManager && selectedManager !== 'all') {
          params.managerId = selectedManager;
        }
        const res = await fieldSalesService.getSalesReps(params);
        if (res.success) setExecutives(res.data);
      } catch (err) {
      }
    };
    fetchExecutives();
  }, [isExecutive, isManagerScope, currentUserId, selectedManager]);

  const fetchSection = useCallback(async (fetcher, setter, loadingSetter) => {
    const result = await fetcher(filterParams, abortRef.current?.signal);
    if (result.canceled) return;
    if (result.success) {
      setter(result.data || (Array.isArray(result.data) ? [] : null));
    } else if (result.error) {
      setError(result.error);
    }
    loadingSetter(false);
  }, [filterParams]);

  useEffect(() => {
    if (dateRange === 'custom' && (!customRange.startDate || !customRange.endDate)) {
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setError(null);

    setKpisLoading(true);
    setRevenueTrendLoading(true);
    setRevenueVsLoading(true);
    setInvoiceTrendLoading(true);
    setInvoiceDistLoading(true);
    setCollectionTrendLoading(true);
    setPaymentMethodsLoading(true);
    setTopPaymentLoading(true);
    setAgingLoading(true);
    setTopOutstandingLoading(true);
    setTopRevenueLoading(true);
    setCustomerDistLoading(true);
    setMonthlySummaryLoading(true);
    setLatestInvoicesLoading(true);
    setLatestPaymentsLoading(true);
    setLargestPaymentsLoading(true);

    if (showExecutiveSummary) {
      setExecutiveSummaryLoading(true);
      fetchSection(financialAnalyticsService.getExecutiveSummary, setExecutiveSummary, setExecutiveSummaryLoading);
    } else {
      setExecutiveSummary([]);
      setExecutiveSummaryLoading(false);
    }

    fetchSection(financialAnalyticsService.getKpis, setKpis, setKpisLoading);
    fetchSection(financialAnalyticsService.getRevenueTrend, setRevenueTrend, setRevenueTrendLoading);
    fetchSection(financialAnalyticsService.getRevenueVsCollections, setRevenueVsCollections, setRevenueVsLoading);
    fetchSection(financialAnalyticsService.getInvoiceTrend, setInvoiceTrend, setInvoiceTrendLoading);
    fetchSection(financialAnalyticsService.getInvoiceDistribution, setInvoiceDistribution, setInvoiceDistLoading);
    fetchSection(financialAnalyticsService.getCollectionTrend, setCollectionTrend, setCollectionTrendLoading);
    fetchSection(financialAnalyticsService.getPaymentMethods, setPaymentMethods, setPaymentMethodsLoading);
    fetchSection(financialAnalyticsService.getTopPaymentCustomers, setTopPaymentCustomers, setTopPaymentLoading);
    fetchSection(financialAnalyticsService.getOutstandingAging, setOutstandingAging, setAgingLoading);
    fetchSection(financialAnalyticsService.getTopOutstanding, setTopOutstanding, setTopOutstandingLoading);
    fetchSection(financialAnalyticsService.getTopRevenueCustomers, setTopRevenueCustomers, setTopRevenueLoading);
    fetchSection(financialAnalyticsService.getCustomerDistribution, setCustomerDistribution, setCustomerDistLoading);
    fetchSection(financialAnalyticsService.getMonthlySummary, setMonthlySummary, setMonthlySummaryLoading);
    fetchSection(financialAnalyticsService.getLatestInvoices, setLatestInvoices, setLatestInvoicesLoading);
    fetchSection(financialAnalyticsService.getLatestPayments, setLatestPayments, setLatestPaymentsLoading);
    fetchSection(financialAnalyticsService.getLargestPayments, setLargestPayments, setLargestPaymentsLoading);

    return () => abortRef.current?.abort();
  }, [filterParams, refreshKey, fetchSection, dateRange, customRange.startDate, customRange.endDate, showExecutiveSummary]);

  const kpiCards = useMemo(() => {
    if (!kpis) return [];
    return [
      { title: 'Total Revenue', value: `₹${formatIndianAmount(kpis.totalRevenue)}`, icon: IndianRupee, color: 'text-emerald-600', bg: 'bg-emerald-50' },
      { title: 'Revenue This Month', value: `₹${formatIndianAmount(kpis.revenueThisMonth)}`, icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50', sub: 'Month to date' },
      {
        title: 'Revenue Growth %',
        value: `${(kpis.revenueGrowthPct || 0).toFixed(1)}%`,
        icon: kpis.revenueGrowthTrend === 'down' ? TrendingDown : TrendingUp,
        color: kpis.revenueGrowthTrend === 'down' ? 'text-red-600' : 'text-emerald-600',
        bg: kpis.revenueGrowthTrend === 'down' ? 'bg-red-50' : 'bg-emerald-50',
        sub: kpis.revenueLastMonthMtd
          ? `vs ₹${formatIndianAmount(kpis.revenueLastMonthMtd)} last month MTD (100% = flat)`
          : 'vs last month MTD'
      },
      { title: 'Total Invoices', value: new Intl.NumberFormat('en-IN').format(kpis.totalInvoices || 0), icon: FileText, color: 'text-violet-600', bg: 'bg-violet-50' },
      { title: 'Average Invoice Value', value: `₹${formatIndianAmount(kpis.averageInvoiceValue)}`, icon: Receipt, color: 'text-indigo-600', bg: 'bg-indigo-50' },
      { title: 'Total Payments Received', value: `₹${formatIndianAmount(kpis.totalPaymentsReceived)}`, icon: CreditCard, color: 'text-teal-600', bg: 'bg-teal-50' },
      { title: 'Outstanding Amount', value: `₹${formatIndianAmount(kpis.totalOutstanding)}`, icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
      { title: 'Collection Rate', value: `${(kpis.collectionRate || 0).toFixed(1)}%`, icon: TrendingUp, color: 'text-cyan-600', bg: 'bg-cyan-50', sub: 'Collected ÷ Revenue' },
      { title: 'Total Customers', value: new Intl.NumberFormat('en-IN').format(kpis.totalCustomers || 0), icon: Users, color: 'text-slate-600', bg: 'bg-slate-50' },
      { title: 'New Customers This Month', value: new Intl.NumberFormat('en-IN').format(kpis.newCustomersThisMonth || 0), icon: UserPlus, color: 'text-pink-600', bg: 'bg-pink-50' },
      { title: 'Average Payment Value', value: `₹${formatIndianAmount(kpis.averagePaymentValue)}`, icon: CreditCard, color: 'text-orange-600', bg: 'bg-orange-50' }
    ];
  }, [kpis]);

  const currencyTooltip = (value, name) => [`₹${formatIndianAmount(value)}`, name];

  const renderHorizontalBarChart = (data, dataKey, nameKey = 'customerName', valueLabel = 'Amount') => {
    if (!data?.length) return <EmptyChart />;
    const chartData = [...data].reverse();
    return (
      <div className="h-[320px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
            <XAxis type="number" tickFormatter={formatSalesAxis} stroke="#94a3b8" fontSize={11} />
            <YAxis type="category" dataKey={nameKey} width={120} stroke="#94a3b8" fontSize={11} tickLine={false} />
            <Tooltip
              contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
              formatter={(val) => [`₹${formatIndianAmount(val)}`, valueLabel]}
            />
            <Bar dataKey={dataKey} fill="#10b981" radius={[0, 4, 4, 0]} barSize={18} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderDonutChart = (data, dataKey, nameKey = 'label') => {
    if (!data?.length) return <EmptyChart />;
    const filtered = data.filter((d) => (d[dataKey] || 0) > 0);
    if (!filtered.length) return <EmptyChart message="No data in selected range" />;
    return (
      <div className="h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={filtered}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={90}
              paddingAngle={3}
              dataKey={dataKey}
              nameKey={nameKey}
            >
              {filtered.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
              formatter={(val, name) => [`₹${formatIndianAmount(val)}`, name]}
            />
            <Legend iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="px-4 py-6 space-y-6">
        {/* Header & Filters */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Financial Analytics</h2>
            <p className="text-sm text-gray-600">Revenue, collections, invoices, and outstanding insights</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg px-3 py-2.5 shadow-sm"
            >
              {DATE_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            {dateRange === 'custom' && (
              <DateRangeFilter
                startDate={customRange.startDate}
                endDate={customRange.endDate}
                onChange={setCustomRange}
              />
            )}

            {!isExecutive && (
              isManager ? (
                <select
                  value={selectedManager}
                  onChange={(e) => {
                    const nextManager = e.target.value;
                    setSelectedManager(nextManager);
                    setSelectedExecutive(nextManager === 'all' ? 'all' : 'manager_only');
                  }}
                  className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg px-3 py-2.5 shadow-sm"
                  disabled={isManager}
                >
                  <option value="all">All Data</option>
                  <option value={currentUserId}>{currentUserName}</option>
                </select>
              ) : (
                <select
                  value={selectedManager}
                  onChange={(e) => {
                    setSelectedManager(e.target.value);
                    setSelectedExecutive('all');
                  }}
                  className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg px-3 py-2.5 shadow-sm"
                >
                  <option value="all">All Managers</option>
                  {managers.map((mgr) => (
                    <option key={mgr._id} value={mgr._id}>{mgr.name}</option>
                  ))}
                </select>
              )
            )}

            {!isExecutive && (
              <select
                value={selectedExecutive}
                onChange={(e) => {
                  const nextExecutive = e.target.value;
                  if (isManager) setSelectedManager(currentUserId);
                  setSelectedExecutive(nextExecutive);
                }}
                className="bg-white border border-gray-300 text-gray-900 text-sm rounded-lg px-3 py-2.5 shadow-sm"
              >
                <option value="all">All Executives</option>
                {(isManager || selectedManager !== 'all') && <option value="my_team">My Team</option>}
                {(isManager || selectedManager !== 'all') && <option value="manager_only">My Data</option>}
                {executives.map((exec) => (
                  <option key={exec._id} value={exec._id}>{exec.name}</option>
                ))}
              </select>
            )}

            <button
              type="button"
              onClick={() => setRefreshKey((k) => k + 1)}
              className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg px-3 py-2.5 shadow-sm hover:bg-gray-50"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 border border-red-200 rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        {/* Executive Summary — visible when a manager is selected */}
        {showExecutiveSummary && (
          <ChartCard title="Executive Summary" subtitle="Per-executive breakdown for the selected manager's team and date range">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Executives</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">Total Invoice</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">Total Amount</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">Received Amount</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">Total Outstanding</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">Last Payment Received</th>
                  </tr>
                </thead>
                {executiveSummaryLoading ? (
                  <TableSkeleton rows={5} cols={6} />
                ) : executiveSummary.length ? (
                  <tbody className="divide-y divide-gray-100">
                    {executiveSummary.map((row) => (
                      <tr key={row.executiveId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900 font-medium">{row.executiveName}</td>
                        <td className="px-4 py-3 text-right text-gray-900">
                          {new Intl.NumberFormat('en-IN').format(row.totalInvoice || 0)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-900">₹{formatIndianAmount(row.totalAmount)}</td>
                        <td className="px-4 py-3 text-right text-gray-900">₹{formatIndianAmount(row.receivedAmount)}</td>
                        <td className="px-4 py-3 text-right text-gray-900">₹{formatIndianAmount(row.totalOutstanding)}</td>
                        <td className="px-4 py-3 text-right text-gray-500">
                          {row.lastPaymentReceivedDate
                            ? formatDate(row.lastPaymentReceivedDate, { includeTime: false })
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                        No executives found for the selected manager
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
            </div>
          </ChartCard>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-4">
          {kpisLoading
            ? Array.from({ length: 11 }).map((_, i) => <KpiSkeleton key={i} />)
            : kpiCards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.title} className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-500">{card.title}</p>
                    <div className={`p-1.5 rounded-md ${card.bg}`}>
                      <Icon className={`h-4 w-4 ${card.color}`} />
                    </div>
                  </div>
                  <p className="text-xl font-semibold text-gray-900">{card.value}</p>
                  {card.sub && <p className="text-xs text-gray-500 mt-1">{card.sub}</p>}
                </div>
              );
            })}
        </div>

        {/* Revenue Analytics */}
        <div>
          <h3 className="text-base font-semibold text-gray-800 mb-3">Revenue Analytics</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Monthly Revenue Trend">
              {revenueTrendLoading ? <SectionLoader /> : revenueTrend.length ? (
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={revenueTrend} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis tickFormatter={formatSalesAxis} stroke="#94a3b8" fontSize={12} />
                      <Tooltip contentStyle={{ borderRadius: '8px' }} formatter={currencyTooltip} />
                      <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name="Revenue" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyChart message="No revenue data in selected range" />}
            </ChartCard>

            <ChartCard title="Revenue vs Collections">
              {revenueVsLoading ? <SectionLoader /> : revenueVsCollections.length ? (
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={revenueVsCollections} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis tickFormatter={formatSalesAxis} stroke="#94a3b8" fontSize={12} />
                      <Tooltip contentStyle={{ borderRadius: '8px' }} formatter={currencyTooltip} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name="Revenue" />
                      <Line type="monotone" dataKey="payments" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="Payments" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyChart />}
            </ChartCard>
          </div>
        </div>

        {/* Invoice Analytics */}
        <div>
          <h3 className="text-base font-semibold text-gray-800 mb-3">Invoice Analytics</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Invoice Trend">
              {invoiceTrendLoading ? <SectionLoader /> : invoiceTrend.length ? (
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={invoiceTrend} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} />
                      <Tooltip contentStyle={{ borderRadius: '8px' }} />
                      <Line type="monotone" dataKey="invoices" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} name="Invoices" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyChart />}
            </ChartCard>

            <ChartCard title="Invoice Value Distribution">
              {invoiceDistLoading ? <SectionLoader /> : renderDonutChart(
                invoiceDistribution.map((d) => ({ label: d.label, amount: d.amount })),
                'amount',
                'label'
              )}
            </ChartCard>
          </div>
        </div>

        {/* Payment Analytics */}
        <div>
          <h3 className="text-base font-semibold text-gray-800 mb-3">Payment Analytics</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            <ChartCard title="Collection Trend" className="xl:col-span-1">
              {collectionTrendLoading ? <SectionLoader /> : collectionTrend.length ? (
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={collectionTrend} margin={{ top: 8, right: 12, left: 4, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis tickFormatter={formatSalesAxis} stroke="#94a3b8" fontSize={12} />
                      <Tooltip contentStyle={{ borderRadius: '8px' }} formatter={currencyTooltip} />
                      <Line type="monotone" dataKey="payments" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} name="Payments" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyChart />}
            </ChartCard>

            <ChartCard title="Payment Method Distribution">
              {paymentMethodsLoading ? <SectionLoader /> : renderDonutChart(paymentMethods, 'amount', 'label')}
            </ChartCard>

            <ChartCard title="Top Customers by Payments">
              {topPaymentLoading ? <SectionLoader height="h-[320px]" /> : renderHorizontalBarChart(
                topPaymentCustomers.map((c) => ({ customerName: c.customerName, amount: c.amount })),
                'amount'
              )}
            </ChartCard>
          </div>
        </div>

        {/* Outstanding Analytics */}
        <div>
          <h3 className="text-base font-semibold text-gray-800 mb-3">Outstanding Analytics</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard
              title="Outstanding Aging"
              subtitle="Approximated by days since invoice date (no due date on invoices)"
            >
              {agingLoading ? <SectionLoader height="h-[280px]" /> : outstandingAging.some((b) => b.amount > 0) ? (
                <div className="h-[280px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={outstandingAging} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tickFormatter={formatSalesAxis} stroke="#94a3b8" fontSize={11} />
                      <YAxis type="category" dataKey="label" width={90} stroke="#94a3b8" fontSize={11} tickLine={false} />
                      <Tooltip formatter={(val) => [`₹${formatIndianAmount(val)}`, 'Outstanding']} contentStyle={{ borderRadius: '8px' }} />
                      <Bar dataKey="amount" fill="#f59e0b" radius={[0, 4, 4, 0]} barSize={22} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <EmptyChart message="No outstanding balances" />}
            </ChartCard>

            <ChartCard title="Top Outstanding Customers">
              {topOutstandingLoading ? <SectionLoader height="h-[320px]" /> : renderHorizontalBarChart(
                topOutstanding.map((c) => ({ customerName: c.customerName, amount: c.amount })),
                'amount'
              )}
            </ChartCard>
          </div>
        </div>

        {/* Customer Financial Insights */}
        <div>
          <h3 className="text-base font-semibold text-gray-800 mb-3">Customer Financial Insights</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Top Customers by Revenue">
              {topRevenueLoading ? <SectionLoader height="h-[320px]" /> : renderHorizontalBarChart(
                topRevenueCustomers.map((c) => ({ customerName: c.customerName, revenue: c.revenue })),
                'revenue',
                'customerName',
                'Revenue'
              )}
            </ChartCard>

            <ChartCard title="Customer Revenue Distribution">
              {customerDistLoading ? <SectionLoader /> : renderDonutChart(customerDistribution, 'revenue', 'label')}
            </ChartCard>
          </div>
        </div>

        {/* Financial Summary Table */}
        <ChartCard title="Financial Summary">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">Month</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Revenue</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Payments</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-500">Outstanding</th>
                </tr>
              </thead>
              {monthlySummaryLoading ? (
                <TableSkeleton rows={6} cols={4} />
              ) : monthlySummary.length ? (
                <tbody className="divide-y divide-gray-100">
                  {monthlySummary.map((row) => (
                    <tr key={row.period} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900">{row.month}</td>
                      <td className="px-4 py-3 text-right text-gray-900">₹{formatIndianAmount(row.revenue)}</td>
                      <td className="px-4 py-3 text-right text-gray-900">₹{formatIndianAmount(row.payments)}</td>
                      <td className="px-4 py-3 text-right text-gray-900">₹{formatIndianAmount(row.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody>
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-400">No summary data for selected range</td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
        </ChartCard>

        {/* Recent Financial Activities */}
        <div>
          <h3 className="text-base font-semibold text-gray-800 mb-3">Recent Financial Activities</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard title="Latest Invoices">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Invoice</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Customer</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Amount</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Date</th>
                    </tr>
                  </thead>
                  {latestInvoicesLoading ? <TableSkeleton cols={4} /> : latestInvoices.length ? (
                    <tbody className="divide-y divide-gray-100">
                      {latestInvoices.map((row, i) => (
                        <tr key={`${row.invoiceNumber}-${i}`} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-900">{row.invoiceNumber}</td>
                          <td className="px-4 py-2 text-gray-700">{row.customerName}</td>
                          <td className="px-4 py-2 text-right text-gray-900">₹{formatIndianAmount(row.amount)}</td>
                          <td className="px-4 py-2 text-right text-gray-500">{formatDate(row.date, { includeTime: false })}</td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No invoices found</td></tr>
                    </tbody>
                  )}
                </table>
              </div>
            </ChartCard>

            <ChartCard title="Latest Payments">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Payment</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Customer</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Amount</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Date</th>
                    </tr>
                  </thead>
                  {latestPaymentsLoading ? <TableSkeleton cols={4} /> : latestPayments.length ? (
                    <tbody className="divide-y divide-gray-100">
                      {latestPayments.map((row, i) => (
                        <tr key={`${row.paymentNumber}-${i}`} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-900">{row.paymentNumber}</td>
                          <td className="px-4 py-2 text-gray-700">{row.customerName}</td>
                          <td className="px-4 py-2 text-right text-gray-900">₹{formatIndianAmount(row.amount)}</td>
                          <td className="px-4 py-2 text-right text-gray-500">{formatDate(row.date, { includeTime: false })}</td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No payments found</td></tr>
                    </tbody>
                  )}
                </table>
              </div>
            </ChartCard>
          </div>

          <div className="mt-4">
            <ChartCard title="Largest Payments">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Customer</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Amount</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Date</th>
                    </tr>
                  </thead>
                  {largestPaymentsLoading ? <TableSkeleton rows={5} cols={3} /> : largestPayments.length ? (
                    <tbody className="divide-y divide-gray-100">
                      {largestPayments.map((row, i) => (
                        <tr key={`${row.paymentNumber}-${i}`} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-900">{row.customerName}</td>
                          <td className="px-4 py-2 text-right text-gray-900">₹{formatIndianAmount(row.amount)}</td>
                          <td className="px-4 py-2 text-right text-gray-500">{formatDate(row.date, { includeTime: false })}</td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-400">No payments found</td></tr>
                    </tbody>
                  )}
                </table>
              </div>
            </ChartCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialAnalytics;
